import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  DeleteSubmissionProductFailure,
  DeleteSubmissionProductInput,
  DeleteSubmissionProductMutation,
} from "@/graphql/codegen/graphql"
import { DeleteSubmissionProductDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import "@/screens/Pricing/graphql/mutations"

interface UseDeleteSubmissionProductReturn {
  deleteSubmissionProduct: (input: DeleteSubmissionProductInput) => Promise<boolean>
  isDeleting: boolean
}

export const useDeleteSubmissionProduct = (): UseDeleteSubmissionProductReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    DeleteSubmissionProductMutation,
    { input: DeleteSubmissionProductInput }
  >(DeleteSubmissionProductDocument)

  const deleteSubmissionProduct = useCallback(
    async (input: DeleteSubmissionProductInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.deleteSubmissionProduct
      const hasError = error || output?.__typename === "DeleteSubmissionProductFailure"

      if (hasError) {
        const message = error?.message ?? (output as DeleteSubmissionProductFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }
      return true
    },
    [executeMutation, notify]
  )

  return { deleteSubmissionProduct, isDeleting: fetching }
}
